// Analytics figures in words (docs/plans/phase3-deeper-analytics.md §5; DESIGN.md §9):
// `KES 12,500`, `42 min`, `2.5 days`, `81.5%`, `22 Sep 2026 14:05`. Nothing rests on colour.
import { formatCount, formatMoney, moneyFromCents } from '@wodi/domain';
import type { AnalyticsUnit, AnalyticsView } from '@wodi/shared';

import { formatWaiting } from '../format/waiting.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';

function countWords(value: string): string {
  const n = Number(value);
  return Number.isSafeInteger(n) ? `${n < 0 ? '-' : ''}${formatCount(Math.abs(n))}` : value;
}

/** A cell or figure as shown. */
export function cellWords(unit: AnalyticsUnit, value: string | null): string {
  if (value === null) return unit === 'instant' ? 'Not yet' : 'No data';
  switch (unit) {
    case 'count':
    case 'number':
      return countWords(value);
    case 'minutes':
      return formatWaiting(Math.round(Number(value)));
    case 'days':
      return `${value} ${value === '1' ? 'day' : 'days'}`;
    case 'percent':
      return `${value}%`;
    case 'money':
      return formatMoney(moneyFromCents(BigInt(value)));
    case 'instant':
      return formatInstant(value);
    case 'day':
      return formatIsoDate(value);
    case 'text':
    case 'id':
      return value;
  }
}

/** Whether a column is right-aligned with tabular figures. */
export function isNumeric(unit: AnalyticsUnit): boolean {
  return ['count', 'number', 'minutes', 'days', 'percent', 'money'].includes(unit);
}

/** A point for a chart: shillings for money, the number otherwise; null draws no bar. */
export function chartValue(unit: AnalyticsUnit, value: string | null): number | null {
  if (value === null) return null;
  return unit === 'money' ? Number(BigInt(value)) / 100 : Number(value);
}

/** "Visits: 2. New patients registered: 3. …": the written summary (DESIGN.md §8). */
export function summarySentence(view: AnalyticsView): string {
  return view.summary.map((s) => `${s.label}: ${cellWords(s.unit, s.value)}.`).join(' ');
}

/** Whether nothing at all was recorded: every figure zero or blank and every table empty. */
export function isEmptyView(view: AnalyticsView): boolean {
  return (
    view.summary.every((s) => s.value === null || s.value === '0') &&
    view.tables.every((t) => t.rows.length === 0)
  );
}
